import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Link2, KeyRound, BadgeCheck, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageShell } from "@/components/Shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ConsentsCard } from "@/components/panels/DocumentsPanel";
import { googleLogin, identityStart } from "@/pages/LoginPage";
import { OperatorDocs, IdentityStep } from "@/components/panels/OperatorDocs";

const LEVEL_LABELS = { L1: "L1 · Registrato", L2: "L2 · Identità verificata", L3: "L3 · Professionista verificato", L4: "L4 · Staff", L5: "L5 · Admin" };

function Card({ title, children, testid }) {
  return <div className="glass space-y-3 rounded-3xl p-5" data-testid={testid}><p className="font-display text-base font-bold text-slate-900">{title}</p>{children}</div>;
}

function Levels({ levels }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="access-levels">
      {Object.entries(LEVEL_LABELS).map(([k, l]) => (
        <span key={k} data-testid={`level-${k}`} className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${levels?.[k] ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-400"}`}>
          {levels?.[k] ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />} {l}{k === "L2" && levels?.L2_sandbox ? " (sandbox)" : ""}
        </span>
      ))}
    </div>
  );
}

function LinkProviders({ identities }) {
  const [pw, setPw] = useState("");
  const hasPw = identities.some((i) => i.provider === "email");
  const start = async (provider) => {
    try {
      const { data } = await api.post("/auth/link/start", { provider, password: pw || undefined });
      if (provider === "google") {
        sessionStorage.setItem("selvix_link_request", data.id);
        googleLogin("/profilo?tab=accesso");
      } else identityStart(provider, "link", data.id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const linked = (p) => identities.some((i) => i.provider === p);
  return (
    <div className="space-y-3">
      {hasPw && <div className="space-y-1"><Label>Conferma la password per collegare un nuovo metodo</Label><Input type="password" data-testid="link-password-input" value={pw} onChange={(e) => setPw(e.target.value)} /></div>}
      <div className="flex flex-wrap gap-2">
        {["spid", "cie", "google"].map((p) => (
          <Button key={p} data-testid={`link-${p}-btn`} disabled={linked(p)} onClick={() => start(p)} variant="outline" className="rounded-full">
            <Link2 className="mr-1.5 h-4 w-4" /> {linked(p) ? `${p.toUpperCase()} collegato` : `Collega ${p.toUpperCase()}${p !== "google" ? " (sandbox)" : ""}`}
          </Button>
        ))}
      </div>
    </div>
  );
}

function MfaCard({ enabled, onChange }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const begin = async () => setSetup((await api.post("/auth/mfa/setup")).data);
  const act = async (path) => {
    try {
      await api.post(`/auth/mfa/${path}`, { code });
      toast.success(path === "enable" ? "MFA attivata" : "MFA disattivata");
      setSetup(null);
      setCode("");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Autenticazione a due fattori (MFA)" testid="mfa-card">
      <p className="text-xs text-slate-500">Obbligatoria per i ruoli privilegiati in produzione. Usa un'app TOTP (Google Authenticator, Authy…).</p>
      {enabled ? (
        <div className="flex gap-2"><Input data-testid="mfa-disable-code" placeholder="Codice" value={code} onChange={(e) => setCode(e.target.value)} /><Button data-testid="mfa-disable-btn" variant="outline" onClick={() => act("disable")}>Disattiva</Button></div>
      ) : setup ? (
        <div className="space-y-2">
          <img src={setup.qr_svg} alt="QR MFA" className="h-40 w-40 rounded-xl bg-white p-2" data-testid="mfa-qr" />
          <p className="font-mono text-xs text-slate-500" data-testid="mfa-secret">{setup.secret}</p>
          <div className="flex gap-2"><Input data-testid="mfa-enable-code" placeholder="Codice a 6 cifre" value={code} onChange={(e) => setCode(e.target.value)} /><Button data-testid="mfa-enable-btn" onClick={() => act("enable")} className="bg-sky-600 hover:bg-sky-700">Attiva</Button></div>
        </div>
      ) : <Button data-testid="mfa-setup-btn" onClick={begin} className="rounded-full bg-sky-600 hover:bg-sky-700"><KeyRound className="mr-1.5 h-4 w-4" /> Configura MFA</Button>}
    </Card>
  );
}

function Sessions() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/auth/sessions").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  return (
    <Card title="Sessioni attive" testid="sessions-card">
      {items.map((s) => (
        <div key={s.sid} className="flex items-center justify-between gap-2 text-xs text-slate-600" data-testid={`session-${s.sid}`}>
          <span className="min-w-0 truncate">{s.method} · {fmtDateTime(s.created_at)} · {s.user_agent.slice(0, 40)} {s.current && <b className="text-emerald-700">(questa)</b>}</span>
          {!s.current && <Button size="sm" variant="outline" data-testid={`session-revoke-${s.sid}`} onClick={() => api.post(`/auth/sessions/${s.sid}/revoke`).then(load)}>Revoca</Button>}
        </div>
      ))}
    </Card>
  );
}

const EMPTY_CRED = { profession: "medico", first_name: "", last_name: "", codice_fiscale: "", degree: "", albo_registered: true, albo_number: "", order_name: "", notes: "" };

function Certification({ levels }) {
  const [v, setV] = useState(null);
  const [f, setF] = useState(EMPTY_CRED);
  const load = () => api.get("/professional/verification").then(({ data }) => setV(data));
  useEffect(() => {
    load();
  }, []);
  const submit = async () => {
    try {
      await api.post("/professional/verification", f);
      toast.success("Richiesta di verifica inviata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const active = v?.status && !["REJECTED", "EXPIRED", "SUSPENDED"].includes(v.status);
  return (
    <Card title="Certificazione professionale" testid="certification-card">
      {!levels?.L2 && <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900" data-testid="certification-needs-l2">Per ottenere il livello L3 è necessario anche verificare l'identità con SPID o CIE (scheda Identità).</p>}
      {v?.status && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid="certification-status">
          Stato: <b data-testid="certification-status-value">{({ PENDING: "In attesa di approvazione", UNDER_REVIEW: "In revisione", VERIFIED: "Approvato", REJECTED: "Rifiutato", SUSPENDED: "Sospeso", EXPIRED: "Scaduto" })[v.status] || v.status}</b> {v.expires_at && `· scade il ${fmtDateTime(v.expires_at)}`} {v.note && `· ${v.note}`}
          <p className="text-xs text-slate-500">Documenti allegati: {v.documents?.length || 0} · Verifica registri: {v.registry_check}</p>
          {["PENDING", "UNDER_REVIEW"].includes(v.status) && <div className="mt-2 space-y-3"><OperatorDocs v={v} reload={load} />{!levels?.L2 && <IdentityStep />}</div>}
        </div>
      )}
      {!active && (
        <div className="grid gap-3 sm:grid-cols-2">
          {[["profession", "Professione"], ["first_name", "Nome"], ["last_name", "Cognome"], ["codice_fiscale", "Codice fiscale"], ["degree", "Titolo di studio"], ["albo_number", "N. iscrizione albo"], ["order_name", "Ordine / ente competente"], ["notes", "Autorizzazioni / note"]].map(([k, l]) => (
            <div key={k} className="space-y-1"><Label>{l}</Label><Input data-testid={`cert-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
          ))}
          <label className="flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={f.albo_registered} onCheckedChange={(x) => setF({ ...f, albo_registered: !!x })} /> Iscritto all'albo</label>
          <Button data-testid="cert-submit-btn" onClick={submit} className="rounded-full bg-sky-600 hover:bg-sky-700 sm:col-span-2"><BadgeCheck className="mr-1.5 h-4 w-4" /> Invia per verifica</Button>
        </div>
      )}
    </Card>
  );
}

function EmailVerification({ user }) {
  const [link, setLink] = useState(null);
  if (user.email_verified) return <p className="text-sm text-emerald-700" data-testid="email-verified">Email verificata: {user.email}</p>;
  return (
    <div className="space-y-2" data-testid="email-not-verified">
      <p className="text-sm text-amber-800">Email non ancora verificata.</p>
      <Button size="sm" data-testid="resend-verification-btn" onClick={async () => { try { setLink((await api.post("/auth/resend-verification")).data.sandbox_email_link); } catch (e) { toast.error(errMsg(e)); } }}>Invia nuovo link</Button>
      {link && <p className="text-xs text-amber-900">EMAIL SIMULATA: <a href={link} data-testid="profile-sandbox-email-link" className="font-bold text-sky-700 underline">apri link di verifica</a></p>}
    </div>
  );
}

export default function ProfilePage() {
  const { user, access, refresh, reloadAccess } = useAuth();
  const [params, setParams] = useSearchParams();
  const [identities, setIdentities] = useState([]);
  useEffect(() => {
    api.get("/auth/identities").then(({ data }) => setIdentities(data));
    reloadAccess();
  }, [reloadAccess]);
  const tab = params.get("tab") || "identita";
  return (
    <PageShell title="Profilo, identità e sicurezza" subtitle="Un unico account, più modalità di accesso. I permessi dipendono da ruolo, livello di identificazione, certificazioni e consensi." testid="profile-page">
      <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })}>
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start">
          {[["identita", "Identità"], ["accesso", "Metodi di accesso"], ["sicurezza", "Sicurezza"], ["certificazione", "Certificazione"], ["consensi", "Consensi"]].map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`profile-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="identita" className="space-y-4">
          <Card title="Livelli di accesso" testid="levels-card"><Levels levels={access?.levels} /></Card>
          <Card title="Verifica identità (SPID / CIE)" testid="identity-card">
            <p className="text-xs text-slate-500">La verifica tramite SPID/CIE porta il profilo al livello L2. Non abilita l'accesso al Fascicolo Sanitario Elettronico nazionale. Attualmente disponibile solo in <b>SANDBOX</b>.</p>
            <LinkProviders identities={identities} />
          </Card>
        </TabsContent>
        <TabsContent value="accesso"><Card title="Metodi collegati" testid="identities-card">
          {identities.map((i) => <p key={i.id} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`identity-${i.provider}`}><ShieldCheck className="h-4 w-4 text-cyan-600" /> {i.provider.toUpperCase()} · {i.email} · {fmtDateTime(i.linked_at)}</p>)}
          <LinkProviders identities={identities} />
        </Card></TabsContent>
        <TabsContent value="sicurezza" className="space-y-4">
          <Card title="Email" testid="email-card"><EmailVerification user={user} /></Card>
          <MfaCard enabled={user.mfa_enabled} onChange={refresh} />
          <Sessions />
        </TabsContent>
        <TabsContent value="certificazione"><Certification levels={access?.levels} /></TabsContent>
        <TabsContent value="consensi"><ConsentsCard /></TabsContent>
      </Tabs>
    </PageShell>
  );
}
